"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";

let pluginsRegistered = false;

interface StatsgraphGraphicProps {
    uid: string;
    Label1?: string;
    Label2?: string;
}
function GraphWithFade() {
    const pathD = "M0 498.328C100.5 498.328 56.5 407.828 180.5 403.328C304.5 398.828 278.5 379.328 339 334.328C399.5 289.328 415 364.83 593.5 310.33C772 255.83 820 39.2593 880.5 4.33008";

    // Gleicher Path, aber nach unten geschlossen für die Füllfläche
    const fillD = pathD + " L880.5 520 L0 520 Z";

    return (
        <svg
            className="absolute right-0 -bottom-6 h-fit w-[75%] -z-20"
            viewBox="0 0 880.5 520"
            width="880"
            height="520"
            style={{ display: "block" }}
        >
            <defs>
                {/* Gradient für die Füllfläche unter der Linie */}
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#45ACE6" stopOpacity="0.4" />
                    <stop offset="70%" stopColor="#45ACE6" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#45ACE6" stopOpacity="0" />
                </linearGradient>

                {/* Mask damit die Linie selbst auch nach unten ausblendet (optional) */}
                <linearGradient id="lineFadeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="white" stopOpacity="1" />
                    <stop offset="85%" stopColor="white" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="white" stopOpacity="0" />
                </linearGradient>
                <mask id="lineFadeMask">
                    <rect x="0" y="0" width="880.5" height="520" fill="url(#lineFadeGrad)" />
                </mask>
            </defs>

            {/* Füllfläche unter der Linie */}
            <path
                d={fillD}
                data-opacity-gsap
                fill="url(#areaGrad)"
            />

            {/* Die Original-Linie mit leichtem Fade nach unten */}
            <path
                d={pathD}
                data-draw-svg
                stroke="#45ACE6"
                strokeWidth="3"
                fill="none"
                mask="url(#lineFadeMask)"
            />
        </svg>
    );
}

function GraphWithFadeDark() {
    const pathD = "M0 233C189 233 196.36 139.541 337.731 155.973C514.445 176.514 672.931 147.243 717.995 94.3513C763.06 41.4594 813.188 5 878 5";
    const fillD = pathD + " L878 250 L0 250 Z";


    return (
        <svg
            className="absolute -right-10 -bottom-4 h-fit w-[75%] -z-10"
            width="878"
            height="238"
            viewBox="0 0 878 250"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <linearGradient id="areaGradDark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#343434" stopOpacity="1" />
                    <stop offset="70%" stopColor="#343434" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#343434" stopOpacity="0" />
                </linearGradient>

                <linearGradient id="lineFadeGradDark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="white" stopOpacity="1" />
                    <stop offset="85%" stopColor="white" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="white" stopOpacity="0" />
                </linearGradient>
                <mask id="lineFadeMaskDark">
                    <rect x="0" y="0" width="878" height="250" fill="url(#lineFadeGradDark)" />
                </mask>
            </defs>

            {/* Füllfläche */}
            <path
                d={fillD}
                data-opacity-gsap
                fill="url(#areaGradDark)"
            />

            {/* Linie */}
            <path
                d={pathD}
                data-draw-svg
                stroke="#343434"
                strokeWidth="3"
                fill="none"
                mask="url(#lineFadeMaskDark)"
            />
        </svg>
    );
}



export default function StatsgraphGraphic({ uid, Label1, Label2 }: StatsgraphGraphicProps) {
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!pluginsRegistered) {
            gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);
            pluginsRegistered = true;
        }

        const root = rootRef.current;

        if (!root) return;

        const ctx = gsap.context(() => {
            const drawPaths = gsap.utils.toArray<SVGPathElement>("[data-draw-svg]");
            const labels = gsap.utils.toArray<HTMLElement>("[data-graph-label]");
            const opacityTargets = gsap.utils.toArray<SVGPathElement>("[data-opacity-gsap]");

            gsap.set(drawPaths, { drawSVG: "0% 0%", opacity: 1 });
            gsap.set(labels, { opacity: 0, y: 16 });
            gsap.set(opacityTargets, { opacity: 0 });

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: root,
                    start: "top 75%",
                    toggleActions: "play none none reverse",
                },
            });

            timeline
                .to(drawPaths, {
                    drawSVG: "0% 100%",
                    duration: 2,
                    ease: "power2.out",
                    stagger: 0.18,
                })
                .to(opacityTargets, {
                    opacity: 1,
                    duration: 1.5,
                    ease: "power2.out",
                }, "<1.5")
                .to(labels, {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power2.out",
                    stagger: 0.08,
                }, "<0.35");
        }, root);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={rootRef} className="overflow-hidden hidden lg:flex pointer-events-none absolute inset-0" data-graph-id={uid}>
            <span data-graph-label className="absolute right-1/6 top-2/4 font-bold text-[#45ACE6]">
                {Label1 || "Basecloud"}
            </span>
            <span data-graph-label className="absolute right-1/6 bottom-1/5 font-bold text-[#A8A8A8]">
                {Label2 || "Andere Anbieter"}
            </span>

            <GraphWithFade />
            <GraphWithFadeDark />

        </div>
    );
}